import * as React from "react"
import GeneratedSite from "../builder/GeneratedSite"
import { load, resolveSite } from "../../lib/site-content"
import * as s from "./RemoteSite.module.css"

/**
 * A customer's website, on their own subdomain — FCS-057.
 * ============================================================================
 * Every customer address serves this same build. This component is what makes
 * that work: it reads the subdomain, fetches that site's JSON from Storage and
 * renders the template the JSON names, with the JSON as the only source of
 * content.
 *
 * ── REMOTE MODE DOES NOT FALL BACK — FCS-057(b) ────────────────────────────
 * If the file is missing, unreadable, published in a shape this build does
 * not know, or asks for a template that is not here, this shows the
 * unavailable page. It never renders the template's built-in sample content,
 * because sample content under a real contractor's domain does not read as an
 * error — it reads as their website, saying things they never said.
 *
 * ── WHY IT IS NOT SERVER-RENDERED ──────────────────────────────────────────
 * The host is GitHub Pages, which serves files. There is no request-time code
 * to read a Host header, so the subdomain can only be known in the browser.
 * That is the trade FCS-057 accepts to ship today: the page renders after one
 * fetch rather than arriving complete.
 */

const RemoteSite = ({ subdomain: known = "" }) => {
  const [state, setState] = React.useState({ status: "loading" })

  React.useEffect(() => {
    let alive = true
    // The page that mounts this has usually resolved the address already; ask again only if it has not.
    Promise.resolve(known || resolveSite(window.location.hostname)).then(load).then(result => {
      if (!alive) return
      setState(
        result.ok
          ? { status: "ready", site: result.site }
          : { status: "unavailable", reason: result.reason }
      )
    })

    return () => {
      alive = false
    }
  }, [])

  if (state.status === "loading") {
    return (
      <div className={s.wait} role="status" aria-live="polite">
        <span className={s.spinner} aria-hidden="true" />
        <span className={s.waitText}>Loading…</span>
      </div>
    )
  }

  if (state.status === "unavailable") {
    return (
      <main className={s.gone}>
        <h1 className={s.goneTitle}>This website is not available</h1>
        <p className={s.goneText}>{state.reason}</p>
        <p className={s.goneFoot}>
          If this is your website,{" "}
          <a href="https://freecontractorservices.com/log-in">log in</a> to check it, or{" "}
          <a href="https://freecontractorservices.com/help/cannot-log-in">get in touch</a>.
        </p>
      </main>
    )
  }

  /* The JSON's `content` is a draft in the shape the templates already read,
     which is why one component can draw both the preview and the live site.
     `plan` comes from the file rather than the draft: what somebody PAID for
     is not the browser's to decide. */
  return <GeneratedSite draft={state.site.content} plan={state.site.plan} mode="remote" />
}

export default RemoteSite
